import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { parse } from 'query-string';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Checkbox from '@ui/forms/Checkbox';
import TextInput from '@ui/forms/TextInput';

import NewInputWrapper from '@ui/molecules/NewInputWrapper';
import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { keysToCamel } from '@utils/butter';
import { membershipOnboardEmailCheck } from '@utils/flowHelpers';
import { get, update } from '@utils/indexedDb';
import { mapKeys } from '@utils/mapKeys';
import { checkbox, requiredCheckbox } from '@utils/validation';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

interface IMembershipBasicInfoStepProps extends IFlowStepProps {}

interface IBasicInfoStepContent {
	title: string;
	subtitle: string;
	firstNameLabel: string;
	lastNameLabel: string;
	nameTooltip: string;
	nameTooltipTitle: string;
	sameLegalNameCopy: string;
	legalFirstNameLabel: string;
	legalLastNameLabel: string;
	legalNameTooltip: string;
	legalNameTooltipTitle: string;
	emailAddressLabel: string;
	termsCopy: string;
	newsletterCopy: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);
const FullWidthForm = fullWidth('form');
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const GradientSpacedColumn = styled(SpacedColumn)`
	background: linear-gradient(
			322.11deg,
			rgba(129, 155, 247, 0.2) 6.91%,
			rgba(240, 163, 214, 0.148) 94.95%
		),
		linear-gradient(0deg, #f0eae2, #f0eae2);
	padding: 3.2rem 1.8rem 3rem;
	margin-left: -1.8rem;
	margin-right: -1.8rem;
	width: auto;
	border-radius: 0.8rem;
`;

const MembershipBasicInfoStep: FC<IMembershipBasicInfoStepProps> = ({ stepKey }) => {
	const location = useLocation();

	const [submitting, setSubmitting] = useState(false);
	const { pageContent } = useStaticQuery(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "basic-membership-info" }) {
					title
					subtitle
					name_tooltip_title
					name_tooltip
					first_name_label
					last_name_label
					same_legal_name_copy
					legal_first_name_label
					legal_last_name_label
					legal_name_tooltip_title
					legal_name_tooltip
					email_address_label
					terms_copy
					newsletter_copy
				}
			}
		`
	);

	const {
		title,
		subtitle,
		firstNameLabel,
		lastNameLabel,
		nameTooltip,
		nameTooltipTitle,
		sameLegalNameCopy,
		legalFirstNameLabel,
		legalLastNameLabel,
		legalNameTooltip,
		legalNameTooltipTitle,
		emailAddressLabel,
		termsCopy,
		newsletterCopy,
	} = keysToCamel(pageContent) as IBasicInfoStepContent;

	const schema = z
		.object({
			preferredFirstName: z.string().min(1, 'Please enter your first name.'),
			preferredLastName: z.string().min(1, 'Please enter your last name.'),
			legalFirstName: z.string().optional(),
			legalLastName: z.string().optional(),
			email: z.string().email(),
			sameLegalName: checkbox,
			receiveNewsletter: checkbox,
			terms: requiredCheckbox('Please read and agree to the above terms.'),
		})
		.refine(
			(input) => {
				if (!(input.legalFirstName && input.legalLastName)) {
					return input.sameLegalName;
				} else {
					return true;
				}
			},
			{
				message:
					'Please attest that your legal name is the same as provided above or enter it below.',
				path: ['sameLegalName'],
			}
		);

	type Schema = Partial<z.input<typeof schema>>;

	const context = useContext(FlowContext);
	const { existingFormData, handleFormSubmission } = context;

	const { handleSubmit, register, formState, watch, reset } = useForm<Schema>({
		defaultValues: existingFormData,
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	useEffect(() => {
		const getInitialData = async () => {
			let data = existingFormData;
			const user = await get('user');
			if (user?.preferredFirstName) {
				data = { ...existingFormData, ...user };
			}
			reset(data);
		};

		getInitialData();
	}, [existingFormData, reset]);

	const { errors, isSubmitted } = formState;

	const onSubmit = async (data) => {
		setSubmitting(true);

		// used ONLY for resending the registration code if needed, and if user needs to come back here
		await update('user', data);

		data = data.sameLegalName
			? { ...data, legalFirstName: data.preferredFirstName, legalLastName: data.preferredLastName }
			: data;

		const { source } = mapKeys((s) => s, parse(location.search, { parseNumbers: true }));

		await membershipOnboardEmailCheck({
			email: data.email,
			legalFirstName: data.legalFirstName,
			legalLastName: data.legalLastName,
			preferredFirstName: data.preferredFirstName,
			preferredLastName: data.preferredLastName,
			receiveNewsletter: data.receiveNewsletter,
			...(source && typeof source === 'string' ? { supportRegister: source } : {}),
		});

		handleFormSubmission(data, stepKey, undefined, location.search).finally(() => {
			setSubmitting(false);
		});
	};

	const sameLegalName = watch('sameLegalName');

	return (
		<>
			<SpacedColumn gap="sm" marginBottom="lg">
				<HeadingText>{title}</HeadingText>
				<BasicText>{subtitle}</BasicText>
			</SpacedColumn>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
				<GradientSpacedColumn marginBottom="lg" gap="md">
					<FlexColumn gap="xs">
						<NewInputWrapper
							titleType="field"
							title={firstNameLabel}
							errorPos="bottom"
							tooltipDescription={nameTooltip}
							tooltipTitle={nameTooltipTitle}>
							<TextInput
								placeholder="First name"
								error={isSubmitted && !!errors.preferredFirstName}
								errorLabel={errors?.preferredFirstName?.message ?? ''}
								{...register('preferredFirstName')}
								type="text"
								fullWidth
							/>
						</NewInputWrapper>
					</FlexColumn>
					<TextInput
						placeholder="Last name"
						label={lastNameLabel}
						error={isSubmitted && !!errors.preferredLastName}
						errorLabel={errors?.preferredLastName?.message ?? ''}
						{...register('preferredLastName')}
						type="text"
						fullWidth
					/>
					<Checkbox
						{...register('sameLegalName')}
						errorLabel={(isSubmitted && errors?.sameLegalName?.message) || ''}
						textSize="xs"
						label={sameLegalNameCopy}
					/>
				</GradientSpacedColumn>
				<SpacedColumn gap="md" marginBottom="xxxl">
					<FlexColumn gap="xs">
						<NewInputWrapper
							titleType="field"
							title={legalFirstNameLabel}
							errorPos="bottom"
							tooltipDescription={legalNameTooltip}
							tooltipTitle={legalNameTooltipTitle}>
							<TextInput
								placeholder=""
								disabled={!!sameLegalName}
								error={isSubmitted && !!errors.legalFirstName}
								errorLabel={errors?.legalFirstName?.message ?? ''}
								{...register('legalFirstName')}
								type="text"
								fullWidth
							/>
						</NewInputWrapper>
					</FlexColumn>
					<TextInput
						placeholder=""
						label={legalLastNameLabel}
						disabled={!!sameLegalName}
						error={isSubmitted && !!errors.legalLastName}
						errorLabel={errors?.legalLastName?.message ?? ''}
						{...register('legalLastName')}
						type="text"
						fullWidth
					/>
					<TextInput
						placeholder="Email address"
						label={emailAddressLabel}
						error={isSubmitted && !!errors.email}
						errorLabel={errors?.email?.message ?? ''}
						{...register('email')}
						type="text"
						fullWidth
					/>
					<Checkbox {...register('receiveNewsletter')} textSize="xs" label={newsletterCopy} />
					<Checkbox
						{...register('terms')}
						errorLabel={(isSubmitted && errors?.terms?.message) || ''}
						textSize="xs"
						optionLabel={termsCopy}
						label={
							<WYSIWYGContent
								textVariant="pillText"
								linkColor="black"
								dangerouslySetInnerHTML={{ __html: termsCopy }}
							/>
						}
					/>
				</SpacedColumn>
				<FullWidthSubmitButton
					label="Next"
					variant="secondary"
					submitting={submitting}
					dataCy="Next"
				/>
			</FullWidthForm>
		</>
	);
};

export { MembershipBasicInfoStep as default };
